'use client';

import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { usePortfolioData } from '@/context/PortfolioDataContext';

export default function SkillsSection() {
  const { t } = useLanguage();
  const { skills } = usePortfolioData();

  const techStack = skills?.length ? skills : [];

  const focusAreas = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <path
            d="M7 8l3 3-3 3"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M13 14h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      ),
      title: t?.skills_backend_title,
      desc: t?.skills_backend_desc,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12H3l9-9 9 9h-2"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <rect x="9" y="14" width="6" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      ),
      title: t?.skills_frontend_title,
      desc: t?.skills_frontend_desc,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2L2 7l10 5 10-5-10-5z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2 17l10 5 10-5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2 12l10 5 10-5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: t?.skills_db_title,
      desc: t?.skills_db_desc,
    },
  ];

  return (
    <section className="relative py-28 px-6 overflow-hidden">
      <div
        className="absolute inset-0 z-0"
        style={{ background: 'linear-gradient(180deg, #1B2432 0%, #141c28 100%)' }}
      />
      <div
        className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none z-0"
        style={{
          width: 500,
          height: 600,
          background: 'radial-gradient(ellipse at left, rgba(99,179,237,0.06) 0%, transparent 70%)',
        }}
      />
      <div className="relative z-10 max-w-5xl mx-auto">
        <div className="reveal flex items-center gap-3 mb-4">
          <div
            className="h-px flex-1 max-w-[48px]"
            style={{ background: 'rgba(99,179,237,0.5)' }}
          />
          <span
            className="text-xs font-semibold uppercase tracking-[0.22em]"
            style={{ color: '#63b3ed' }}
          >
            {t?.skills_label}
          </span>
        </div>

        <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 reveal-delay-1">
          <h2
            className="font-display font-bold"
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.25rem)',
              color: '#EDF2F7',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            {t?.skills_heading_1}{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #63b3ed, #a78bfa)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t?.skills_heading_2}
            </span>
          </h2>
          <p
            className="text-base max-w-sm"
            style={{ color: 'rgba(237,242,247,0.5)', lineHeight: 1.7 }}
          >
            {t?.skills_subtitle}
          </p>
        </div>

        <div className="reveal reveal-delay-2 grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {focusAreas?.map((area, i) => (
            <div
              key={i}
              className="glow-card rounded-2xl p-6"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{
                  background: 'rgba(99,179,237,0.1)',
                  border: '1px solid rgba(99,179,237,0.25)',
                  color: '#63b3ed',
                }}
              >
                {area?.icon}
              </div>
              <h3
                className="font-display font-semibold text-base mb-2"
                style={{ color: '#EDF2F7' }}
              >
                {area?.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(237,242,247,0.5)' }}>
                {area?.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="reveal reveal-delay-3 flex flex-wrap gap-2.5">
          {techStack?.map((tech) => (
            <span
              key={tech?.id || tech?.name}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all duration-300 hover:scale-105"
              style={{
                background: tech?.bg_color || 'rgba(255,255,255,0.04)',
                border: `1px solid ${tech?.border_color || 'rgba(200,206,214,0.12)'}`,
                color: tech?.color || '#EDF2F7',
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: tech?.color || '#EDF2F7', opacity: 0.8 }}
              />
              {tech?.name}
              <span
                className="text-xs font-normal opacity-50"
                style={{ color: tech?.color || '#EDF2F7' }}
              >
                {tech?.category}
              </span>
            </span>
          ))}
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(200,206,214,0.12), transparent)',
        }}
      />
    </section>
  );
}
